import React, { useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Search, UserPlus, Phone, Calendar, Copy, Pill, BookOpen, AlertTriangle, ArrowRight, Clock, Heart } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast, Toaster } from "sonner";
import UpcomingCalendar from "../components/UpcomingCalendar";
import VaccineRecallsPanel from "../components/VaccineRecallsPanel";
import RefillNudgesPanel from "../components/RefillNudgesPanel";
import SuggestVaccineDialog from "../components/SuggestVaccineDialog";
import { computeAgeYears } from "../lib/ageUtils";
import EditPatientDialog from "../components/EditPatientDialog";
import { PatientLanguageSelect } from "../components/PatientLanguageSelect";

export default function Dashboard() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [mobile, setMobile] = useState("");
  const [dob, setDob] = useState("");
  const [results, setResults] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", age: "", dob: "", sex: "Male", address: "", referred_by: "", wa_opt_in: true, preferred_language: "mr" });
  const [familyConfirm, setFamilyConfirm] = useState(null); // {family_members: [...]} pending confirmation

  const run = async (nextQ = q, nextMob = mobile, nextDob = dob) => {
    // Require at least one of: q>=2 chars, mobile>=4 digits, dob non-empty
    const digits = (nextMob || "").replace(/\D/g, "");
    if ((nextQ || "").trim().length < 2 && digits.length < 4 && !nextDob) {
      setResults([]); setDuplicates([]); return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (nextQ) params.set("q", nextQ);
      if (nextMob) params.set("mobile", nextMob);
      if (nextDob) params.set("dob", nextDob);
      const r = await api.get(`/patients/search?${params.toString()}`);
      setResults(r.data.results || []);
      setDuplicates(r.data.duplicates || []);
    } catch (e) { console.debug("[silent]", e); }
    setLoading(false);
  };
  const search = (val) => { setQ(val); run(val, mobile, dob); };
  const onMobile = (val) => { setMobile(val); run(q, val, dob); };
  const onDob = (val) => { setDob(val); run(q, mobile, val); };

  const createPatient = async (confirmFamily = false) => {
    if (!form.name || !form.mobile) { toast.error("Name and mobile are required"); return; }
    if (form.dob) {
      const todayIso = new Date().toISOString().slice(0, 10);
      if (form.dob > todayIso) { toast.error("Date of birth cannot be in the future"); return; }
    }
    try {
      const r = await api.post("/patients", { ...form, confirm_family_new: confirmFamily });
      if (r.data.needs_family_confirm) {
        // Existing patients share this mobile — confirm this is a new family
        // member before creating a fresh record. Keeps the doctor from
        // accidentally duplicating a returning patient.
        setFamilyConfirm({ family_members: r.data.family_members || [] });
        return;
      }
      if (r.data.duplicate) toast.info("Existing patient found — opening record");
      else if (r.data.family_members?.length) toast.success(`Registered new family member — ${r.data.family_members.length} other patient(s) share this mobile`);
      else toast.success("Patient registered");
      setOpen(false);
      setFamilyConfirm(null);
      nav(`/patient/${r.data.patient.patient_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save patient");
    }
  };

  return (
    <Layout>
      <Toaster position="top-right" richColors />
      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-8">
          <div className="overline">PATIENT LOOKUP</div>
          <h1 className="font-heading text-3xl font-semibold mt-1 text-slate-900">Find or register a patient</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-lg">
            Search by mobile number, patient name or patient ID. Existing records will load history,
            allergies, previous medicines and investigations automatically.
          </p>

          <div className="mt-6 space-y-2">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="patient-search-input"
                  value={q}
                  onChange={(e) => search(e.target.value)}
                  placeholder="Name or Patient ID…"
                  className="pl-9 h-11"
                />
              </div>
              <Button data-testid="new-patient-btn" onClick={() => setOpen(true)} className="h-11 bg-pink-600 hover:bg-pink-700 text-white gap-2">
                <UserPlus size={16} /> New Patient
              </Button>
              <Button variant="outline" data-testid="open-education-nav-btn" onClick={() => nav('/education')} className="h-11 gap-2 border-pink-300 text-pink-700 hover:bg-pink-50">
                <BookOpen size={16} /> Education
              </Button>
            </div>
            <div className="flex gap-2 items-center">
              <div className="relative flex-1">
                <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="patient-search-mobile"
                  value={mobile}
                  onChange={(e) => onMobile(e.target.value)}
                  placeholder="Mobile number (any 4+ digits)…"
                  className="pl-9 h-10 text-sm tabular"
                  inputMode="tel"
                />
              </div>
              <div className="relative flex-1">
                <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="patient-search-dob"
                  type="date"
                  max={new Date().toISOString().slice(0, 10)}
                  value={dob}
                  onChange={(e) => onDob(e.target.value)}
                  placeholder="Date of birth"
                  className="pl-9 h-10 text-sm tabular"
                />
              </div>
              {(q || mobile || dob) && (
                <button
                  onClick={() => { setQ(""); setMobile(""); setDob(""); setResults([]); setDuplicates([]); }}
                  className="text-[11px] text-slate-500 hover:text-slate-800 px-2 shrink-0"
                  data-testid="patient-search-clear"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="text-[11px] text-slate-500">
              Combine <strong>Name</strong> with <strong>Date of birth</strong> or the exact <strong>Mobile</strong> for a precise match in a busy clinic. Any two fields narrow the results.
            </div>
          </div>

          {duplicates.length > 0 && (
            <div className="mt-3 border border-amber-300 bg-amber-50 rounded-md px-3 py-2 text-xs text-amber-900 flex items-start gap-2" data-testid="dup-warning">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <div>
                <strong>Duplicate names detected</strong> — {duplicates.length} name{duplicates.length > 1 ? "s" : ""} appear multiple times. Add a <strong>Date of birth</strong> or the exact <strong>Mobile</strong> to disambiguate before opening a record.
              </div>
            </div>
          )}

          <div className="mt-3 bg-white border border-slate-200 rounded-md overflow-hidden">
            {loading && <div className="p-4 text-sm text-slate-500">Searching…</div>}
            {!loading && results.length === 0 && (q.length >= 2 || (mobile.replace(/\D/g, "").length >= 4) || dob) && (
              <div className="p-6 text-sm text-slate-500">No matching patients found. Widen the search or register a new patient.</div>
            )}
            {!loading && results.length === 0 && !q && !mobile && !dob && (
              <div className="p-6 text-sm text-slate-500">Type at least 2 characters — or enter a mobile / DOB — to search.</div>
            )}
            {results.map((p) => (
              <div
                key={p.patient_id}
                data-testid={`patient-row-${p.patient_id}`}
                className={`w-full border-b border-slate-100 hover:bg-slate-50/70 group ${p.duplicate_name ? "bg-amber-50/40" : ""}`}
              >
                <button
                  onClick={() => nav(`/patient/${p.patient_id}`)}
                  className="w-full text-left px-4 py-3 flex items-start justify-between gap-3"
                  data-testid={`patient-open-${p.patient_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 flex items-center gap-1.5">
                      {p.name}
                      {p.duplicate_name && (
                        <span className="text-[9px] uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold" title="Another patient shares this name. Verify DOB or mobile.">DUP</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 flex gap-3 mt-0.5 flex-wrap">
                      <span className="tabular">{p.patient_id}</span>
                      <span className="flex items-center gap-1"><Phone size={11} /> {p.mobile}</span>
                      {p.age && <span>{p.age} yr / {p.sex}</span>}
                      {p.dob && <span className="tabular flex items-center gap-1"><Calendar size={10} /> {new Date(p.dob).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}</span>}
                    </div>
                    {p.last_visit ? (
                      <div className="mt-1.5 text-[11px] flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-600" data-testid={`last-rx-summary-${p.patient_id}`}>
                        <span className="inline-flex items-center gap-1">
                          <BookOpen size={11} className="text-pink-600" />
                          <strong className="text-slate-800">{p.last_visit.primary || "—"}</strong>
                        </span>
                        {p.last_visit.top_medicines?.length > 0 && (
                          <span className="inline-flex items-center gap-1 truncate">
                            <Pill size={11} className="text-slate-400" />
                            <span className="truncate">{p.last_visit.top_medicines.join(", ")}</span>
                            {p.last_visit.medicine_count > 3 && (
                              <span className="text-slate-400">+{p.last_visit.medicine_count - 3}</span>
                            )}
                          </span>
                        )}
                        <span className="text-slate-400 tabular">
                          {p.last_visit.visit_date && new Date(p.last_visit.visit_date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" })}
                        </span>
                      </div>
                    ) : (
                      <div className="mt-1.5 text-[11px] text-slate-400 italic">No previous visits</div>
                    )}
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1.5">
                    <EditPatientDialog
                      patient={p}
                      iconSize={12}
                      onSaved={(upd) => setResults((rs) => rs.map((x) => (x.patient_id === upd.patient_id ? { ...x, ...upd } : x)))}
                    />
                    {p.last_visit && (
                      <button
                        onClick={(e) => { e.stopPropagation(); nav(`/patient/${p.patient_id}?clone=${p.last_visit.visit_id}`); }}
                        className="px-2 py-1 text-[11px] rounded border border-pink-300 text-pink-700 hover:bg-pink-50 flex items-center gap-1"
                        data-testid={`clone-last-rx-${p.patient_id}`}
                        title={`Clone last prescription (${p.last_visit.medicine_count} meds) into a new visit`}
                      >
                        <Copy size={11} /> Clone last Rx
                      </button>
                    )}
                    <div className="text-[10px] text-slate-400 tabular">
                      {p.updated_at && new Date(p.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-4 space-y-3">
          <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="overline">Today</div>
              <div className="mt-0.5 font-heading text-lg font-semibold flex items-center gap-2 text-slate-900">
                <Calendar size={16} className="text-pink-600 shrink-0" />
                <span className="truncate">{new Date().toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}</span>
              </div>
            </div>
            <SuggestVaccineDialog />
          </div>
          <IntakeQueueTile />
          <UpcomingCalendar
            testid="dashboard-calendar"
            days={90}
            onOpenPatient={(pid) => nav(`/patient/${pid}`)}
          />
          <VaccineRecallsPanel onOpenPatient={(pid) => nav(`/patient/${pid}`)} />
          <RefillNudgesPanel onOpenPatient={(pid) => nav(`/patient/${pid}`)} />
          <div className="alert-info rounded-md p-3 text-[11px] leading-relaxed">
            <strong className="font-semibold">Tip:</strong> Type a mobile number to auto-load a returning patient — previous vitals, medicines and reports load side by side.
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-patient-dialog" className="max-w-md max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Register new patient</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="overline">FULL NAME</Label>
              <Input data-testid="new-patient-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <div className="mt-3"><PatientLanguageSelect value={form.preferred_language} onChange={(v) => setForm({ ...form, preferred_language: v })} testId="new-patient-language" label="Default patient language" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">MOBILE</Label>
                <Input data-testid="new-patient-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
                <label className="flex items-center gap-1.5 text-[10.5px] text-slate-600 mt-1.5 cursor-pointer select-none">
                  <input type="checkbox" checked={form.wa_opt_in} onChange={(e) => setForm({ ...form, wa_opt_in: e.target.checked })} className="accent-emerald-600" data-testid="new-patient-wa-optin" />
                  Consents to WhatsApp messages (reminders, reports, education)
                </label>
              </div>
              <div>
                <Label className="overline">AGE</Label>
                <Input
                  data-testid="new-patient-age"
                  value={form.age}
                  onChange={(e) => setForm({ ...form, age: e.target.value })}
                  placeholder={form.dob ? "auto from DOB" : ""}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">DATE OF BIRTH</Label>
                <Input
                  type="date"
                  max={new Date().toISOString().slice(0, 10)}
                  value={form.dob}
                  onChange={(e) => {
                    const dob = e.target.value;
                    const derived = computeAgeYears(dob);
                    // Auto-fill AGE from DOB. If the doctor had typed a
                    // custom age, prefer DOB — the whole point of this fix.
                    setForm((prev) => ({ ...prev, dob, age: derived != null ? String(derived) : prev.age }));
                  }}
                  className="tabular"
                  data-testid="new-patient-dob"
                />
              </div>
              <div>
                <Label className="overline">SEX</Label>
                <Select value={form.sex} onValueChange={(v) => setForm({ ...form, sex: v })}>
                  <SelectTrigger data-testid="new-patient-sex"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="overline">REFERRED BY</Label>
              <Input value={form.referred_by} onChange={(e) => setForm({ ...form, referred_by: e.target.value })} />
            </div>
            <div>
              <Label className="overline">ADDRESS</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => createPatient(false)} data-testid="save-new-patient-btn" className="bg-pink-600 hover:bg-pink-700 text-white">Save & Open</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Family-member confirmation — same mobile already belongs to another patient */}
      <Dialog open={!!familyConfirm} onOpenChange={(v) => !v && setFamilyConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="family-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">This mobile already has a patient</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-3">
            <p>
              <strong>{form.mobile}</strong> is already used by {familyConfirm?.family_members?.length ?? 0} existing patient
              {familyConfirm?.family_members?.length === 1 ? "" : "s"}. Is <strong>{form.name}</strong> a different family member?
            </p>
            <ul className="text-xs bg-slate-50 border border-slate-200 rounded-md p-2 space-y-1 max-h-40 overflow-auto">
              {(familyConfirm?.family_members || []).map((f) => (
                <li key={f.patient_id} className="flex justify-between gap-3" data-testid={`family-existing-${f.patient_id}`}>
                  <span className="truncate">
                    <strong>{f.name}</strong> {f.dob && <span className="text-slate-500">· DOB {f.dob}</span>} {f.sex && <span className="text-slate-500">· {f.sex}</span>}
                  </span>
                  <span className="tabular text-slate-500">{f.patient_id}</span>
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFamilyConfirm(null)} data-testid="family-confirm-cancel">Cancel</Button>
            <Button
              onClick={() => createPatient(true)}
              className="bg-pink-600 hover:bg-pink-700 text-white"
              data-testid="family-confirm-add"
            >
              Yes, add new family member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}


// Live waiting-list of today's reception-completed intakes. Auto-refreshes
// every 8 seconds so the doctor sees the queue grow without touching F5.
function IntakeQueueTile() {
  const nav = useNavigate();
  const [data, setData] = React.useState({ queue: [], waiting: 0, total_intake_today: 0 });
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/dashboard/intake-queue");
        if (!cancelled) { setData(r.data); setLoading(false); }
      } catch { if (!cancelled) setLoading(false); }
    };
    load();
    const t = setInterval(load, 8000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="intake-queue-tile">
      <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-emerald-600" />
          <div className="font-heading font-semibold text-sm text-slate-900">Intake Queue</div>
          <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5 font-semibold">
            {data.waiting} waiting
          </span>
        </div>
        <span className="text-[10px] text-slate-400">
          {data.total_intake_today} today · auto-refresh 8 s
        </span>
      </div>
      <div className="max-h-72 overflow-auto">
        {loading && <div className="p-6 text-center text-xs text-slate-400">Loading…</div>}
        {!loading && data.queue.length === 0 && (
          <div className="p-6 text-center text-xs text-slate-400">
            No patients waiting — reception hasn't sent an intake yet today.
          </div>
        )}
        {data.queue.map((r) => {
          const v = r.vitals || {};
          const stale = r.updated_at ? Math.round((Date.now() - new Date(r.updated_at).getTime()) / 60000) : 0;
          return (
            <button
              key={r.visit_id}
              onClick={() => nav(`/patient/${r.patient_id}`)}
              className="w-full text-left border-b border-slate-100 last:border-b-0 px-4 py-2.5 hover:bg-emerald-50/40 transition-colors"
              data-testid={`intake-queue-row-${r.patient_id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {r.photo_url ? (
                    <img src={r.photo_url} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-200 shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-[10px] font-semibold text-slate-600 shrink-0">
                      {(r.patient_name || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{r.patient_name || "Unnamed"}</div>
                    <div className="text-[10px] text-slate-500 tabular">
                      {r.patient_age && <>Age {r.patient_age} · </>}{r.patient_sex} · {r.patient_id}
                    </div>
                  </div>
                </div>
                <ArrowRight size={13} className="text-slate-400 mt-1 shrink-0" />
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
                {v.sbp && v.dbp && (
                  <span className="tabular"><Heart size={9} className="inline mr-0.5 text-red-500" /> {v.sbp}/{v.dbp}</span>
                )}
                {v.pulse && <span className="tabular">PR {v.pulse}</span>}
                {v.spo2 && <span className="tabular">SpO₂ {v.spo2}%</span>}
                {v.weight && v.height && <span className="tabular">{v.weight}kg / {v.height}cm</span>}
                {v.bmi && <span className="tabular">BMI {v.bmi}</span>}
                <span className="ml-auto text-[10px] text-slate-400">
                  {stale === 0 ? "just now" : `${stale} min ago`}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
